const THUMBNAIL_SIZE = 300;

/**
 * Attaches a hover-triggered thumbnail preview to `element`: immediately on
 * hover, shows a floating box with a small loader, then calls
 * `fetchImageUrl` and swaps the loader for the image once it resolves (or
 * removes the box if there is no image). Every caller passes a
 * `fetchImageUrl` backed by `CoreImageViewService.getFirstImageUrl`, which
 * already caches per entity (and can be invalidated) — this function
 * deliberately does not cache on its own, since a second, per-element cache
 * here couldn't be invalidated when images change elsewhere (e.g. deleting
 * an image in the edit form) and would keep showing stale results. Returns
 * a cleanup function that removes the listeners and any visible preview.
 */
export function attachImageHoverPreview(
  element: HTMLElement,
  fetchImageUrl: () => Promise<string | null | undefined>
): () => void {
  let previewEl: HTMLElement | null = null;
  let token = 0;

  const removePreview = (): void => {
    previewEl?.remove();
    previewEl = null;
  };

  const positionPreview = (): void => {
    if (!previewEl) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const boxRect = previewEl.getBoundingClientRect();

    let top = rect.bottom + 6;
    if (top + boxRect.height > window.innerHeight) {
      top = rect.top - boxRect.height - 6;
    }
    let left = rect.left;
    if (left + boxRect.width > window.innerWidth) {
      left = window.innerWidth - boxRect.width - 8;
    }

    previewEl.style.top = `${Math.max(4, top)}px`;
    previewEl.style.left = `${Math.max(4, left)}px`;
  };

  const showBox = (content: HTMLElement): void => {
    removePreview();

    const box = document.createElement('div');
    box.style.cssText = `
      position: fixed;
      z-index: 2000;
      padding: 4px;
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
      pointer-events: none;
      line-height: 0;
    `;
    box.appendChild(content);
    document.body.appendChild(box);
    previewEl = box;
    positionPreview();
  };

  const showLoader = (): void => {
    const loaderWrap = document.createElement('div');
    loaderWrap.style.cssText = `
      width: ${THUMBNAIL_SIZE}px;
      height: ${THUMBNAIL_SIZE}px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    const spinner = document.createElement('i');
    spinner.className = 'la la-spinner la-spin la-2x text-primary';
    loaderWrap.appendChild(spinner);
    showBox(loaderWrap);
  };

  const showImage = (url: string): void => {
    const img = document.createElement('img');
    img.style.cssText = `
      display: block;
      width: ${THUMBNAIL_SIZE}px;
      height: ${THUMBNAIL_SIZE}px;
      object-fit: cover;
      border-radius: 2px;
    `;
    img.onload = positionPreview;
    img.src = url;
    showBox(img);
  };

  const onEnter = (): void => {
    const myToken = ++token;

    showLoader();
    fetchImageUrl()
      .then((url) => {
        if (myToken !== token) {
          return;
        }
        if (url) {
          showImage(url);
        } else {
          removePreview();
        }
      })
      .catch(() => {
        if (myToken === token) {
          removePreview();
        }
      });
  };

  const onLeave = (): void => {
    token++;
    removePreview();
  };

  element.addEventListener('mouseenter', onEnter);
  element.addEventListener('mouseleave', onLeave);

  return () => {
    removePreview();
    element.removeEventListener('mouseenter', onEnter);
    element.removeEventListener('mouseleave', onLeave);
  };
}
